import { ProjectId, Snapshot } from "@custom-types/base-types";
import { SdbMemberTypes } from "@custom-types/member-types";
import {
  PaginatedProjects,
  ProjectArchivingState,
  ProjectSettings,
  SdbProject,
} from "@custom-types/project-types";
import { createSelector } from "@reduxjs/toolkit";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { projectsAdapter } from "@store/projects/projects-slice";
import {
  ProjectsList,
  ProjectsListType,
  ProjectsState,
} from "@store/projects/projects-slice-types";
import { RootState } from "@store/store-helper";
import { StringEntityId } from "@store/store-types";
import { hasUserValidPermissionProjectLevel } from "@utils/permission-control/project-permission-control";
import { RequiredPermissionProjectLevelName } from "@utils/permission-control/project-permission-control-types";
import {
  getProjectArchivingState,
  getProjectManager,
  getProjectManagerName,
  isFeatureEnabled,
  isProjectDemo,
} from "@utils/project-utils";

/** The project list state plus the resolved projects array */
type ProjectListSelectorResult = ProjectsList &
  Pick<PaginatedProjects, "projects">;

/**
 * Returns all projects
 */
export const projectsSelector: (state: RootState) => SdbProject[] =
  createSelector(
    (state: RootState) => state.projects,
    (projectsState) => {
      return projectsAdapter.getSelectors().selectAll(projectsState);
    }
  );

/**
 * Returns all projects mapped by their id
 */
export const projectsMapByIdSelector: (
  state: RootState
) => Map<ProjectId, SdbProject> = createSelector(
  (state: RootState) => state.projects.entities,
  (projects: Record<StringEntityId, SdbProject>) => {
    return new Map(Object.entries(projects));
  }
);

/**
 * Returns the ids of all projects
 */
export const projectIdsSelector: (state: RootState) => ProjectId[] =
  createSelector(
    (state: RootState) => state.projects,
    (projectsState) => {
      return projectsState.ids;
    }
  );

/**
 * Returns true if a given project can be archived.
 *
 * @param project
 */
export function isProjectArchivingAllowedSelector(
  project: SdbProject
): (state: RootState) => boolean {
  const getIsProjectAllowedToBeArchived = isFeatureEnabledForProjectSelector(
    project.id,
    "project-archive"
  );

  return createSelector(
    isProjectActiveSelector(project.id),
    getIsProjectAllowedToBeArchived,
    (isActive, isAllowed) => {
      return isActive && isAllowed;
    }
  );
}

/**
 * Returns true if a given project can be unarchived.
 *
 * @param project
 */
export function isProjectUnarchivingAllowedSelector(
  project: SdbProject
): (state: RootState) => boolean {
  const getIsProjectAllowedToBeUnarchived = isFeatureEnabledForProjectSelector(
    project.id,
    "project-unarchive"
  );

  return createSelector(
    projectArchivingStateSelector(project.id),
    getIsProjectAllowedToBeUnarchived,
    (archivingState, isAllowed) => {
      return (
        archivingState === ProjectArchivingState.archived &&
        isAllowed &&
        project.archivingState !== "ARCHIVED_DOWNLOADED"
      );
    }
  );
}

/**
 * Returns the fetching properties of the projects slice.
 */
export const fetchingProjectsFlagsSelector: (
  state: RootState
) => ProjectsState["fetching"] = createSelector(
  (state: RootState) => state.projects,
  (projectsState) => projectsState.fetching
);

/**
 * Returns the deleted projects map of the projects slice
 */
export const deletedProjectsSelector: (
  state: RootState
) => ProjectsState["deletedProjects"] = createSelector(
  (state: RootState) => state.projects,
  (projectsState) => projectsState.deletedProjects
);

/**
 * Returns true if a given a project is currently ongoing a process.
 *
 * @param projectId
 */
export function isProjectProcessingSelector(
  projectId: ProjectId
): (state: RootState) => boolean {
  return createSelector(
    (state: RootState) => state.projects.fetching.processingProjects,
    (processingProjects) => {
      return processingProjects[projectId];
    }
  );
}

/**
 * Get project details by providing the projectId
 */
export function getProjectByIdSelector(
  projectId: ProjectId | undefined
): (state: RootState) => SdbProject | null {
  return createSelector(
    (state: RootState) => state.projects,
    (projects) => {
      if (!projectId) {
        return null;
      }
      return (
        projectsAdapter.getSelectors().selectById(projects, projectId) ?? null
      );
    }
  );
}

/**
 * Get the selected project using the selected project Id and finding it on the store,
 * returns null if there's no projectId or if the project could not be found in the store.
 */
export const selectedProjectSelector: (state: RootState) => SdbProject | null =
  createSelector(
    (state: RootState) => state.projects.selectedProject.id,
    (state: RootState) => state.projects,
    (selectedProjectId, projects) => {
      if (!selectedProjectId) {
        return null;
      }

      return (
        projectsAdapter
          .getSelectors()
          .selectById(projects, selectedProjectId) ?? null
      );
    }
  );

/**
 * Get the selected project ID
 */
export const selectedProjectIdSelector: (state: RootState) => ProjectId | null =
  createSelector(
    (state: RootState) => state.projects.selectedProject,
    (selectedProject) => {
      return selectedProject.id;
    }
  );

/**
 * Returns the snapshots of the selected project from the project entity
 */
export const selectedProjectSnapshotsSelector: (
  state: RootState
) => Snapshot[] = createSelector(selectedProjectSelector, (project) => {
  return project?.snapshots ?? [];
});

/**
 * Returns whether the snapshots of the selected project are currently being fetched
 */
export const isFetchingProjectSnapshotsSelector: (state: RootState) => boolean =
  createSelector(
    (state: RootState) => state.projects,
    (projectsState) => projectsState.fetching.isFetchingProjectSnapshots
  );

/**
 * Returns the number of snapshots that are currently being created for the selected project
 */
export const snapshotsBeingCreatedCountSelector: (state: RootState) => number =
  createSelector(
    (state: RootState) => state.projects,
    (projectsState) => projectsState.selectedProject.snapshotsBeingCreatedCount
  );

/**
 * Returns the members of the currently selected snapshot
 */
export const selectedSnapshotMembersSelector: (
  state: RootState
) => FaroCoreApiTypes.ProjectMemberBase[] = createSelector(
  (state: RootState) => state.projects,
  (projectsState) => projectsState.selectedProject.selectedSnapshotMembers
);

/**
 * Returns the snapshot selected to display in the side panel
 */
export const selectedSnapshotSelector: (
  state: RootState
) => Snapshot | undefined = createSelector(
  (state: RootState) => state.projects.selectedProject.selectedSnapshotId,
  selectedProjectSelector,
  (selectedSnapshotId, project) => {
    if (!selectedSnapshotId) {
      return undefined;
    }
    return project?.snapshots.find(
      (snapshot) => snapshot.id === selectedSnapshotId
    );
  }
);

/**
 * Returns whether the snapshot side panel is open
 */
export const isSnapshotSidePanelOpenSelector: (state: RootState) => boolean =
  createSelector(
    (state: RootState) => state.projects,
    (projectsState) => projectsState.selectedProject.isSnapshotSidePanelOpen
  );

/**
 * @returns The snapshot entity with the specified id, or null if it does not exist in the store
 * @param snapshotId - The ID of the snapshot to select
 */
export function getSnapshotByIdSelector(
  snapshotId: Snapshot["id"]
): (state: RootState) => Snapshot | null {
  return createSelector(
    (state: RootState) => selectedProjectSnapshotsSelector(state),
    (snapshotsState) => {
      return (
        snapshotsState.find((snapshot) => snapshot.id === snapshotId) ?? null
      );
    }
  );
}

/**
 * Returns the project members
 */
export const projectMembersSelector: (
  state: RootState
) => FaroCoreApiTypes.ProjectMemberBase[] = createSelector(
  selectedProjectSelector,
  (selectedProject) => {
    return selectedProject?.members ?? [];
  }
);

/**
 * Get project member with the provided role
 */
export function projectMembersByRoleSelector(
  userRole: FaroCoreApiTypes.ProjectRole
): (state: RootState) => FaroCoreApiTypes.ProjectMemberBase[] {
  return createSelector(projectMembersSelector, (selectedMembers) => {
    return selectedMembers.filter(({ role }) => role === userRole);
  });
}

/**
 * Get project member with the provided role
 */
export function isFeatureEnabledForProjectSelector(
  projectId: ProjectId,
  feature: FaroCoreApiTypes.FeatureIdentifier
): (state: RootState) => boolean {
  return createSelector(getProjectByIdSelector(projectId), (project) => {
    if (!project || project.features === null) {
      return false;
    }

    return isFeatureEnabled(project, feature);
  });
}

/**
 * Returns the archiving state of a project
 */
export function projectArchivingStateSelector(
  projectId: ProjectId
): (state: RootState) => ProjectArchivingState | null {
  return createSelector(getProjectByIdSelector(projectId), (project) => {
    if (!project) {
      return null;
    }

    return project.archivingState === "UNARCHIVED"
      ? ProjectArchivingState.active
      : ProjectArchivingState.archived;
  });
}

/**
 * Returns whether the project is active or not
 */
export function isProjectActiveSelector(
  projectId: ProjectId
): (state: RootState) => boolean {
  return createSelector(
    projectArchivingStateSelector(projectId),
    (archivingState) => {
      return archivingState === ProjectArchivingState.active;
    }
  );
}

/**
 * Returns whether the project is archived or not
 */
export function isProjectArchiveSelector(
  projectId: ProjectId
): (state: RootState) => boolean {
  return createSelector(
    projectArchivingStateSelector(projectId),
    (archivingState) => {
      return archivingState === ProjectArchivingState.archived;
    }
  );
}

/**
 * Returns true if selected project is editable
 * Projects can be editable if: User is allowed to edit them and they are active
 */
export function isSelectedProjectEditableSelector(): (
  state: RootState
) => boolean {
  return createSelector(selectedProjectSelector, (selectedProject) => {
    if (selectedProject) {
      const hasUserPermissionToEditProject = hasUserValidPermissionProjectLevel(
        {
          permissionName:
            RequiredPermissionProjectLevelName.canEditProjectDetails,
          selectedProject,
        }
      );

      const isProjectActive =
        getProjectArchivingState(selectedProject.archivingState) ===
        ProjectArchivingState.active;

      const isDemoProject = isProjectDemo(selectedProject);

      return (
        hasUserPermissionToEditProject && isProjectActive && !isDemoProject
      );
    }

    return false;
  });
}

/**
 * Gets the project manager and its name for the selected project.
 *
 * @returns An object including the project manager of the project,
 * and the prettified version of the name.
 */
export const selectedProjectManagerSelector: (state: RootState) => {
  user: SdbMemberTypes | null;
  name: string;
} = createSelector(selectedProjectSelector, (selectedProject) => {
  const user = selectedProject ? getProjectManager(selectedProject) : null;
  const name = selectedProject ? getProjectManagerName(selectedProject) : "";
  return { user, name };
});

/**
 * Returns the project context of the selected project.
 */
export const selectedProjectContextSelector: (
  state: RootState
) => FaroCoreApiTypes.GetProjectContext | null = createSelector(
  (state: RootState) => state.projects.selectedProject,
  (selectedProject) => {
    return selectedProject.context;
  }
);

/**
 * Returns the settings of the selected project.
 */
export const selectedProjectSettingsSelector: (
  state: RootState
) => ProjectSettings | null = createSelector(
  (state: RootState) => state.projects.selectedProject,
  (selectedProject) => {
    return selectedProject.settings;
  }
);

/**
 * @returns whether the annotations of the current project should be displayed for viewers
 */
export const shouldDisplayMarkupsForViewersSelector: (
  state: RootState
) => boolean = createSelector(selectedProjectSettingsSelector, (settings) => {
  return settings?.shouldDisplayMarkupsForViewers ?? false;
});

/**
 * @returns the project list for the given list type
 * @param listType Type of the list of projects
 */
export function projectsListSelector(
  listType: ProjectsListType
): (state: RootState) => ProjectListSelectorResult {
  return createSelector(
    (state: RootState) => state.projects.entities,
    (state: RootState) => state.projects.lists,
    (allProjectsMap, lists) => {
      const list = lists[listType];
      const projects = list.ids.map((id) => allProjectsMap[id]).filter(Boolean);

      return {
        projects,
        ids: list.ids,
        nextPageCursor: list.nextPageCursor,
        hasFetchedFirstPage: list.hasFetchedFirstPage,
        totalCount: list.totalCount,
      };
    }
  );
}
